import { Helmet } from 'react-helmet-async';
import { Fragment, type ReactNode } from 'react';

const COLLABORATOR_TERMS_TEXT = `## PREÂMBULO

O presente instrumento, denominado "Termos e Condições de Uso — Colaborador" ("**Termos**"), regula o acesso e a utilização da plataforma digital **Plumaa**, de titularidade de **ARAUJO & SILVA LTDA**, pessoa jurídica de direito privado, inscrita no CNPJ sob o nº **65.321.886/0001-10**, com sede na Rua Pais Leme, nº 215, Conj. 1713, Bairro Pinheiros, São Paulo/SP, CEP 05.424-150, nome fantasia **PLUMAA** (doravante "**Plumaa**"), por parte de pessoa física vinculada a uma **Empresa Contratante** que tenha firmado contrato empresarial com a Plumaa (modalidade **B2B2C**).

Estes Termos, em conjunto com a **Política de Privacidade** da Plumaa, constituem contrato eletrônico vinculante entre a Plumaa e o **Colaborador**. Ao acessar, cadastrar-se, preencher formulários ou utilizar qualquer funcionalidade da Plataforma, o Colaborador declara, de forma livre, informada e inequívoca, que leu, compreendeu e concorda integralmente com todas as disposições aqui previstas.

**Caso o Colaborador não concorde com qualquer disposição destes Termos, deverá abster-se, imediatamente, de acessar ou utilizar a Plataforma.**

## CLÁUSULA 1 — DEFINIÇÕES

1.1. Para os fins destes Termos:

**Plataforma** ou **Plumaa**: ambiente digital operado pela Plumaa, que (i) aplica aos Colaboradores, sob demanda da Empresa Contratante, formulários cientificamente validados de avaliação de saúde mental e riscos psicossociais (COPSOQ, CBI e outros instrumentos eventualmente agregados), (ii) disponibiliza, aos Colaboradores que optarem, planos B2B2C de benefícios em saúde (teleterapia, teleconsulta médica, nutrição, bem-estar digital e correlatos), e (iii) entrega à Empresa Contratante dashboards e relatórios **agregados e anonimizados** para apoio à conformidade com a **Norma Regulamentadora nº 01 (NR-01)** e ao Programa de Gerenciamento de Riscos (PGR).

**Empresa Contratante**: pessoa jurídica que contrata os serviços da Plumaa em favor de seus colaboradores, por meio de contrato corporativo específico.

**Colaborador** ou **Você**: pessoa física maior de 18 (dezoito) anos, em pleno gozo de sua capacidade civil, vinculada à Empresa Contratante por relação de emprego, estágio, terceirização, prestação de serviços ou outra relação análoga, a quem a Empresa Contratante tenha facultado o acesso à Plataforma.

**Profissional da Saúde** ou **Profissional**: pessoa física ou jurídica, regularmente inscrita e habilitada em seu respectivo conselho de classe (CRP, CRM ou CRN), cadastrada na Plataforma para prestar serviços de teleatendimento.

**Plano B2B2C**: plano individual de benefícios em saúde digital contratado, pago e mantido **diretamente pelo Colaborador** junto à Plumaa (p. ex., Lite, Go, Standard, Premium), de adesão **facultativa**.

**Formulários**: instrumentos padronizados e cientificamente validados (COPSOQ — Copenhagen Psychosocial Questionnaire, CBI — Copenhagen Burnout Inventory, entre outros) aplicados na Plataforma.

**Dados Agregados/Anonimizados**: conjunto de dados tratados de modo que o titular não possa ser identificado, nos termos do art. 5º, III, da LGPD, conforme detalhado na Cláusula 7.

**LGPD**: Lei Geral de Proteção de Dados Pessoais (Lei nº 13.709/2018).

**NR-01**: Norma Regulamentadora nº 01 do Ministério do Trabalho e Emprego, em sua redação vigente, que trata de disposições gerais e gerenciamento de riscos ocupacionais, incluindo riscos psicossociais.

## CLÁUSULA 2 — ADESÃO E INDEPENDÊNCIA CONTRATUAL

2.1. A utilização da Plataforma constitui manifestação livre, informada e inequívoca de aceitação destes Termos e da Política de Privacidade.

2.2. **Ausência de Relação Empregatícia.** Estes Termos constituem **contrato eletrônico de uso de plataforma digital** entre Plumaa e Colaborador. **Em nenhuma hipótese** se estabelece, em razão destes Termos ou do uso da Plataforma, vínculo empregatício, societário, de subordinação, estágio, prestação de serviços ou qualquer outra relação jurídica entre o Colaborador e a Plumaa.

2.3. **Separação em Relação à Empresa Contratante.** A relação do Colaborador com a Empresa Contratante (de natureza trabalhista, estatutária ou civil) é **autônoma e independente** destes Termos. Eventuais disputas entre Colaborador e Empresa Contratante **não envolverão a Plumaa**, que, em tais hipóteses, atuará apenas como operadora de dados ou controladora, conforme a natureza do tratamento, nos termos da LGPD.

2.4. **Alterações dos Termos.** A Plumaa poderá alterar estes Termos a qualquer tempo. Alterações substanciais serão comunicadas por e-mail cadastrado ou aviso ostensivo na Plataforma, com antecedência razoável, sendo a continuidade do uso interpretada como aceitação integral.

## CLÁUSULA 3 — NATUREZA DOS SERVIÇOS AO COLABORADOR

3.1. Por meio do acesso disponibilizado pela Empresa Contratante, o Colaborador poderá:

a) **Responder os Formulários** aplicados pela Plataforma (COPSOQ, CBI e outros);

b) **Visualizar suas próprias métricas individuais** derivadas das respostas, conforme escopo do plano ativo (na camada gratuita, apenas métrica agregada renomeada "**Energia**", que **não constitui diagnóstico clínico**);

c) **Aderir facultativamente** a um dos Planos B2B2C, mediante pagamento mensal no cartão de crédito, para obter descontos em consultas, sessões de teleterapia, teleconsultas médicas, nutrição e benefícios correlatos;

d) **Agendar teleconsultas** com Profissionais da Saúde cadastrados, conforme regras da Cláusula 5.

3.2. **Caráter Informativo dos Formulários.** Os Formulários são **instrumentos de rastreio e monitoramento de riscos psicossociais**, aplicados para fins de conformidade regulatória (NR-01), evolução da experiência do Colaborador e entrega de dashboards corporativos agregados. **Não constituem diagnóstico médico ou psicológico**, não substituem avaliação clínica individual e não devem ser interpretados como resultado de consulta em saúde.

3.3. **Ausência de Serviços Emergenciais.** Aplica-se, mutatis mutandis, a Cláusula 3.3 dos Termos do Usuário: a Plumaa **não é serviço de emergência**. Em situações de risco à vida ou à integridade, o Colaborador deverá buscar imediatamente SAMU (192), Polícia Militar (190), CVV (188) ou hospital/pronto-socorro mais próximo.

## CLÁUSULA 4 — FACULTATIVIDADE, CONSENTIMENTO E QUESTÕES PULÁVEIS

4.1. **Participação Facultativa.** A adesão do Colaborador à Plataforma é **facultativa**. A não utilização não poderá, por si só, gerar qualquer consequência disciplinar, trabalhista, retaliatória ou discriminatória imposta pela Empresa Contratante, sendo responsabilidade exclusiva desta última observar a legislação trabalhista e de proteção de dados aplicável.

4.2. **Perguntas Sensíveis.** O Colaborador poderá, a seu critério, **pular** determinadas perguntas que considere sensíveis, especialmente aquelas que envolvam dados pessoais sensíveis (saúde mental, orientação sexual, filiação religiosa, dados genéticos, entre outros, nos termos do art. 5º, II, da LGPD). **A ausência de resposta a perguntas específicas não impede o uso das demais funcionalidades**, ressalvando-se que a precisão do dashboard individual do Colaborador poderá ser afetada.

4.3. **Consentimento Específico.** O tratamento de dados pessoais sensíveis (saúde mental) pela Plumaa se dará mediante **consentimento específico e destacado** do Colaborador, nos termos do art. 11, I, da LGPD, e, conforme o caso, também sob a base legal de **tutela da saúde** (art. 11, II, "f", da LGPD), **proteção da vida ou da incolumidade física** (art. 11, II, "e"), ou **cumprimento de obrigação legal** (art. 11, II, "a"), conforme detalhado na Política de Privacidade.

4.4. **Revogação do Consentimento.** O Colaborador poderá revogar o consentimento a qualquer tempo, pelos canais oficiais (Cláusula 16), sendo alertado de que a revogação poderá limitar ou impossibilitar o uso de funcionalidades que dependam do tratamento autorizado.

## CLÁUSULA 5 — PLANOS B2B2C, AGENDAMENTOS E PAGAMENTOS

5.1. **Planos B2B2C.** O Colaborador poderá, de forma **facultativa e por iniciativa própria**, contratar um dos Planos B2B2C disponíveis, por preço mensal divulgado na Plataforma. A contratação:

a) **é individual e direta entre Colaborador e Plumaa**, sem interferência da Empresa Contratante;

b) é paga mensalmente por cartão de crédito ou outro meio disponível;

c) pode ser cancelada pelo Colaborador a qualquer tempo, sem multa, com efeitos a partir do próximo ciclo de cobrança;

d) não gera obrigação da Empresa Contratante em subsidiar, reembolsar ou custear o valor, salvo disposição expressa no contrato empresarial firmado entre Empresa e Plumaa.

5.2. **Agendamento de Consultas.** A marcação de teleconsultas (terapia, medicina, nutrição) segue as mesmas regras aplicáveis ao Usuário (Cláusulas 5, 6 e 7 dos Termos do Usuário Comum), aplicáveis por referência.

5.3. **Tolerância, Reagendamento e Cancelamento.** Observam-se os mesmos critérios do Termo do Usuário:

- Tolerância de atraso: **15 minutos**;
- Cancelamento gratuito: **com antecedência igual ou superior a 24h** do horário agendado;
- Cancelamento com menos de 24h: sujeito à anuência do Profissional; se negada, a sessão será considerada realizada.

5.4. **Sessões Gratuitas do Plano.** As sessões gratuitas previstas no plano contratado (quando aplicável) não são cumulativas e expiram ao final de cada ciclo mensal, salvo disposição em contrário expressa na Plataforma.

## CLÁUSULA 6 — OBRIGAÇÕES E CONDUTA DO COLABORADOR

6.1. O Colaborador obriga-se a:

a) utilizar a Plataforma de forma ética, lícita e adequada, conforme estes Termos e a legislação vigente;

b) prestar informações verídicas, completas e atualizadas no cadastro;

c) não compartilhar credenciais de acesso com terceiros, incluindo outros colaboradores da Empresa Contratante;

d) tratar Profissionais e equipe de suporte com respeito, civilidade e urbanidade;

e) não utilizar a Plataforma como meio de registro, denúncia ou documentação de conflitos trabalhistas com a Empresa Contratante — canais apropriados devem ser utilizados para tais fins (Ministério Público do Trabalho, sindicatos, canais internos da empresa, entre outros);

f) não gravar, reproduzir, divulgar ou publicar (em qualquer mídia, rede social, grupo ou canal) áudio, vídeo, imagem ou conteúdo de teleconsulta sem a autorização expressa e por escrito do Profissional;

g) não utilizar a Plataforma como via de disseminação de informação inverídica, difamatória, ofensiva ou discriminatória contra a Empresa Contratante, outros Colaboradores, Profissionais ou terceiros.

6.2. **Condutas Expressamente Vedadas.** É vedado ao Colaborador:

a) **responder dolosamente aos Formulários com informações falsas** com a intenção de distorcer resultados corporativos, retaliar a Empresa Contratante, prejudicar colegas ou manipular métricas;

b) **preencher o cadastro em nome de terceiros** (inclusive outro colaborador), ou permitir que terceiros respondam em seu lugar;

c) praticar **assédio, ofensa, ameaça, coação, discriminação** (por raça, cor, etnia, religião, sexo, orientação sexual, identidade de gênero, deficiência, idade ou qualquer outra condição) ou qualquer forma de violência contra Profissionais, colaboradores da Plumaa ou outros Colaboradores, durante a consulta, chat, e-mail, redes sociais ou qualquer outro canal;

d) **manter tratamento clínico com Profissional da Plataforma fora do ambiente da Plumaa**, seja por pagamento direto, contato pessoal ou qualquer outro arranjo que contorne a intermediação;

e) utilizar a Plataforma para finalidades **não previstas** (p. ex., realização de divulgação comercial, recrutamento, venda de produtos, proselitismo político ou religioso, captação de pacientes particulares para fins profissionais próprios);

f) tentar acessar, de qualquer forma, dados de outros Colaboradores, dashboards corporativos agregados, painéis administrativos, áreas restritas ou credenciais não autorizadas;

g) utilizar bots, crawlers, engenharia reversa, injeção de código, vírus ou qualquer forma de tentativa de invasão, modificação ou comprometimento da Plataforma;

h) praticar qualquer conduta que possa comprometer a segurança, a reputação, a integridade operacional ou a conformidade regulatória da Plumaa, da Empresa Contratante, dos Profissionais ou de outros Colaboradores;

i) **reproduzir, publicar ou divulgar**, em redes sociais, sites, blogs, imprensa ou qualquer outro meio, prints, trechos, capturas de tela, dados, métricas, dashboards, relatórios ou informações obtidas da Plataforma, inclusive as suas próprias, se tal divulgação puder expor dados de terceiros ou informações confidenciais da Empresa Contratante ou da Plumaa.

## CLÁUSULA 7 — TRATAMENTO DE DADOS, ANONIMIZAÇÃO E DASHBOARDS CORPORATIVOS

7.1. **Dupla Camada de Dados.** O Colaborador reconhece expressamente que a operação da Plataforma envolve duas camadas distintas de tratamento:

a) **Camada Individual (identificada)**: acessível apenas pelo próprio Colaborador e, quando autorizado ou necessário, pelo Profissional da Saúde por ele escolhido, para fins de visualização de suas próprias métricas, acompanhamento clínico e funcionamento da conta;

b) **Camada Corporativa (agregada e anonimizada)**: entregue à Empresa Contratante em forma de dashboards, relatórios e laudos **agregados e anonimizados** — isto é, tratados de modo que não seja possível identificar, por meios razoáveis, o Colaborador individual (art. 5º, III, e art. 12 da LGPD).

7.2. **Garantia de Anonimização.** A Plumaa compromete-se a:

a) não entregar à Empresa Contratante, em nenhuma hipótese, respostas individualizadas, nome, CPF, e-mail, identidade ou qualquer dado que permita identificar, direta ou indiretamente, o Colaborador respondente;

b) aplicar técnicas de **agregação estatística**, limiares mínimos de amostra (quando o número de respondentes em um setor for muito baixo, a Plumaa **omitirá o recorte** para evitar reidentificação) e demais salvaguardas técnicas e administrativas apropriadas;

c) manter o conteúdo das teleconsultas completamente fora do alcance da Empresa Contratante.

7.3. **Exceções Legais e Emergenciais.** A Plumaa poderá, excepcionalmente, compartilhar dados identificáveis com a Empresa Contratante, com o profissional responsável técnico indicado pela empresa ou com autoridades competentes, **exclusivamente** nas hipóteses de:

a) **risco iminente à vida** do Colaborador ou de terceiros, nos termos do art. 7º, IV, e art. 11, II, "e", da LGPD (proteção da vida ou incolumidade física);

b) **ordem judicial**, requisição administrativa legítima ou cumprimento de obrigação legal/regulatória, nos termos do art. 7º, II e VI, e art. 11, II, "a", da LGPD;

c) **consentimento específico e expresso** do Colaborador para compartilhamento identificado, manifestado por meio de declaração destacada.

7.3.1. Nas hipóteses das alíneas "a" e "b" acima, a Plumaa envidará esforços razoáveis para notificar previamente o Colaborador, salvo quando a comunicação for vedada por lei ou possa frustrar a finalidade da medida.

7.4. **Dados Sensíveis de Saúde.** As respostas aos Formulários, ao envolverem avaliação de saúde mental, são consideradas **dados pessoais sensíveis** (art. 5º, II, da LGPD). Seu tratamento observará as bases legais específicas do art. 11 da LGPD.

7.5. **Direitos do Titular.** O Colaborador poderá, a qualquer tempo, exercer os direitos previstos no art. 18 da LGPD (confirmação, acesso, correção, anonimização, bloqueio, eliminação, portabilidade, revogação do consentimento, informação de compartilhamento etc.), mediante solicitação aos canais oficiais (Cláusula 16) ou ao Encarregado (DPO) da Plumaa.

7.6. **Controladoria e Operação.** Nos termos da LGPD:

a) no tratamento dos dados relativos ao cadastro, uso da Plataforma, contratação de Planos B2B2C e execução de teleconsultas, a **Plumaa atua como Controladora**;

b) no tratamento dos dados coletados via Formulários aplicados por demanda da Empresa Contratante, as partes poderão atuar como **Controladoras em conjunto** ou **Operadora e Controladora** (a definir no contrato corporativo), sempre com vistas à proteção do titular;

c) os detalhes estão descritos na Política de Privacidade da Plumaa e, quando aplicável, em adendo de tratamento de dados firmado com a Empresa Contratante.

## CLÁUSULA 8 — SIGILO DAS CONSULTAS E AUSÊNCIA DE ACESSO EMPRESARIAL

8.1. **Inviolabilidade do Conteúdo Clínico.** O conteúdo (áudio e vídeo) das teleconsultas é **estritamente confidencial** entre Colaborador e Profissional. A Plumaa **não monitora, grava ou armazena** tais conteúdos.

8.2. **Ausência de Acesso pela Empresa Contratante.** A Empresa Contratante **não terá, em nenhuma hipótese**, acesso a:

a) conteúdo de teleconsultas;
b) respostas individualizadas a Formulários;
c) prontuário, registros clínicos ou dados individuais de atendimento;
d) informação sobre a adesão individual do Colaborador a um Plano B2B2C (salvo agregação corporativa anônima de adesão);
e) identidade de Colaboradores que tenham pulado ou recusado determinadas perguntas.

## CLÁUSULA 9 — USO INDEVIDO, CONDUTAS ABUSIVAS E MEDIDAS

9.1. **Perfis Inautênticos, Fraudes e Manipulação.** Caso a Plumaa identifique indícios fundados de:

a) preenchimento fraudulento ou manipulação deliberada de respostas;
b) múltiplas contas, uso de identidade de terceiros ou simulação;
c) conluio entre Colaboradores para distorcer resultados corporativos;

a Plumaa poderá, a seu exclusivo critério, **desconsiderar** (excluir da amostra) as respostas envolvidas, suspender a conta e adotar outras medidas previstas nestes Termos, sem prejuízo de comunicar a Empresa Contratante **em termos agregados e gerais** (sem expor identidade) e/ou, em hipótese de crime, as autoridades competentes.

9.2. **Ofensa, Assédio e Ameaça a Profissional.** Registros fundados de ofensa, ameaça, assédio (moral, sexual ou de qualquer natureza), discriminação ou violência verbal/psicológica contra Profissional da Saúde, durante ou após a teleconsulta, ensejarão:

a) suspensão imediata da conta do Colaborador;
b) análise do caso concreto com direito ao contraditório em prazo razoável;
c) possível **descredenciamento definitivo** do Colaborador da Plataforma;
d) **ressarcimento do Profissional e/ou da Plumaa** pelos danos materiais, morais ou reputacionais causados;
e) **comunicação à Empresa Contratante**, nos termos agregados ou, nos casos graves, identificados, quando houver justa causa sob a LGPD (p. ex., tutela da vida, cumprimento legal, interesse público) e/ou **cominação legal**;
f) **comunicação às autoridades policiais**, quando o fato configurar, em tese, ilícito penal (injúria, calúnia, difamação, ameaça, assédio, importunação sexual, racismo, entre outros).

9.3. **Uso Compulsivo ou Preocupante.** Quando a Plumaa, objetivamente, identificar padrões de uso que possam indicar agravamento grave de quadro de saúde mental com risco iminente, poderá acionar os protocolos da Cláusula 7.3 (exceções legais e emergenciais), privilegiando a **proteção da vida** do Colaborador.

9.4. **Divulgação Indevida de Conteúdo.** A reprodução ou divulgação não autorizada de áudios, vídeos, imagens, prints, trechos ou dados da teleconsulta ou do dashboard em redes sociais, imprensa ou qualquer outro meio poderá ensejar:

a) encerramento imediato da conta;
b) responsabilização civil do Colaborador por danos materiais, morais e reputacionais, à Plumaa, à Empresa Contratante, ao Profissional e a terceiros;
c) medidas cíveis e criminais cabíveis (violação de sigilo, direito de imagem, propriedade intelectual, entre outras), nos termos da Lei nº 9.610/1998, art. 20 do Código Civil, e legislação penal aplicável.

## CLÁUSULA 10 — RESPONSABILIDADE DA PLUMAA E LIMITAÇÕES

10.1. A Plumaa, em regime de obrigação de meio, compromete-se a:

a) manter a Plataforma funcional e segura, ressalvadas interrupções programadas e falhas de terceiros;
b) adotar medidas técnicas e administrativas razoáveis de segurança da informação;
c) disponibilizar canal de atendimento e de exercício de direitos do titular.

10.2. A Plumaa **não se responsabiliza**, sob qualquer fundamento, por:

a) qualidade, eficácia ou consequência dos atos clínicos praticados pelos Profissionais;

b) condutas do Colaborador perante a Empresa Contratante ou perante terceiros, decorrentes ou relacionadas ao uso da Plataforma;

c) eventual decisão disciplinar, trabalhista ou gerencial adotada pela Empresa Contratante com base em informações **agregadas e anonimizadas** entregues pela Plumaa, as quais, por sua natureza, **não permitem a identificação individualizada** do Colaborador;

d) interpretação pelo Colaborador das métricas e dashboards da Plataforma como diagnóstico clínico individual — reitera-se que os Formulários **não substituem avaliação clínica**;

e) indisponibilidades ou falhas causadas por serviços de terceiros (Google, Stripe, provedores de nuvem, operadoras de telecomunicação);

f) danos indiretos, lucros cessantes, perda de chance ou dano reflexo não diretamente atribuíveis à operação da Plataforma.

10.3. **Teto de Responsabilidade.** Na máxima extensão permitida pela legislação aplicável, a responsabilidade eventual da Plumaa perante o Colaborador estará limitada ao valor que o Colaborador tenha efetivamente pago à Plumaa nos 12 (doze) meses anteriores ao evento (na hipótese de Plano B2B2C contratado), ressalvadas as normas cogentes do CDC.

10.4. **Isenção de Relação Trabalhista.** A Plumaa não é responsável, em nenhuma hipótese e sob nenhum fundamento, por obrigações trabalhistas, previdenciárias, fiscais ou de qualquer outra natureza entre o Colaborador e a Empresa Contratante, inclusive quanto a demissão, rescisão, estabilidade, acidente de trabalho ou doença ocupacional, ainda que o fato guarde relação temática com as métricas oferecidas pela Plataforma.

## CLÁUSULA 11 — INDENIZAÇÃO POR USO INDEVIDO

11.1. O Colaborador compromete-se a **indenizar, manter indene e defender** a Plumaa, a Empresa Contratante, os Profissionais da Saúde e demais terceiros envolvidos de qualquer reclamação, demanda, ação judicial, multa, sanção administrativa ou prejuízo (incluindo honorários advocatícios razoáveis) decorrentes:

a) da violação, pelo Colaborador, destes Termos ou da legislação aplicável;

b) de conduta dolosa ou culposa grave do Colaborador que cause dano à Plataforma ou a qualquer de suas partes;

c) de divulgação, reprodução ou vazamento, por ato do Colaborador, de conteúdo confidencial obtido por meio da Plataforma.

## CLÁUSULA 12 — SUSPENSÃO E ENCERRAMENTO DA CONTA

12.1. **Por Iniciativa do Colaborador.** O Colaborador poderá solicitar, a qualquer tempo, o encerramento de sua conta, mediante contato pelos canais oficiais. O encerramento implicará desativação do perfil e tratamento dos dados conforme a Política de Privacidade.

12.2. **Por Iniciativa da Plumaa.** A Plumaa poderá suspender ou encerrar a conta do Colaborador, unilateralmente, nas hipóteses de:

a) violação destes Termos ou da Política de Privacidade;
b) qualquer uma das condutas vedadas na Cláusula 6.2 e 9;
c) fraude, uso de identidade de terceiros ou cadastro inautêntico;
d) ordem judicial, requisição administrativa ou cumprimento de obrigação legal;
e) encerramento do contrato entre Empresa Contratante e Plumaa, hipótese em que o acesso do Colaborador às funcionalidades corporativas será descontinuado (com ressalva de continuidade de Plano B2B2C em curso, a critério do Colaborador, se assim permitir o modelo comercial vigente).

12.3. **Efeitos do Encerramento.** O encerramento não exime o Colaborador de obrigações financeiras em aberto (mensalidade de Plano B2B2C já incorrida, consultas efetivamente realizadas) nem afasta responsabilização por atos anteriores ao encerramento.

## CLÁUSULA 13 — PROPRIEDADE INTELECTUAL

13.1. A Plataforma, suas marcas, código-fonte, interfaces, dashboards, relatórios, textos, imagens, metodologias e demais elementos são de titularidade exclusiva da Plumaa e/ou de seus licenciantes, protegidos pela legislação aplicável.

13.2. A utilização da Plataforma concede ao Colaborador apenas **licença limitada, pessoal, intransferível, não exclusiva e revogável** para uso das funcionalidades, nos termos destes Termos.

13.3. É vedado ao Colaborador reproduzir, modificar, distribuir, comercializar, fazer engenharia reversa, descompilar ou criar obras derivadas, bem como utilizar marca, logotipo ou identidade da Plumaa sem autorização prévia e por escrito.

## CLÁUSULA 14 — DISPONIBILIDADE, FORÇA MAIOR E COMUNICAÇÕES

14.1. Aplicam-se ao Colaborador, por referência, as Cláusulas 14 (Disponibilidade), 15 (Força Maior) e 16 (Comunicações) dos Termos do Usuário Comum, no que couber.

## CLÁUSULA 15 — SOBREVIVÊNCIA

15.1. Sobreviverão ao encerramento destas relações as disposições que, por natureza, assim o exigirem, incluindo: dever de sigilo, obrigações de indenização, limitação de responsabilidade, propriedade intelectual e foro de eleição.

## CLÁUSULA 16 — CANAIS OFICIAIS DE ATENDIMENTO

16.1. Canais oficiais:

- **E-mail:** contato@plumaa.com.br
- **WhatsApp oficial:** [número a ser confirmado]
- **Encarregado de Dados (DPO):** contato@plumaa.com.br

16.2. Os prazos de resposta observarão a razoabilidade e, quando aplicável, os prazos legais (LGPD, CDC etc.).

## CLÁUSULA 17 — DISPOSIÇÕES GERAIS

17.1. **Independência das Cláusulas.** A eventual nulidade de qualquer cláusula não afetará a validade das demais.

17.2. **Não Renúncia.** A tolerância quanto ao descumprimento não constituirá novação ou renúncia.

17.3. **Cessão.** É vedado ao Colaborador ceder seus direitos e obrigações. A Plumaa poderá cedê-los em caso de reorganização societária, mediante comunicação.

17.4. **Integralidade.** Estes Termos, somados à Política de Privacidade, constituem o acordo integral entre as partes.

## CLÁUSULA 18 — LEI APLICÁVEL E FORO

18.1. Estes Termos são regidos pelas leis da **República Federativa do Brasil**.

18.2. Fica eleito o **Foro da Comarca de São Paulo, Estado de São Paulo**, como competente para dirimir quaisquer controvérsias, com renúncia expressa a qualquer outro, ressalvadas as hipóteses de competência absoluta em favor do consumidor (CDC).

18.3. As partes comprometem-se, previamente, à tentativa de composição amigável por mediação antes de ajuizamento judicial.

## ACEITE ELETRÔNICO

Ao clicar em **"Li e aceito os Termos de Uso do Colaborador"**, ou ao efetivamente utilizar a Plataforma, o Colaborador manifesta sua aceitação livre, informada, expressa e inequívoca a todas as disposições destes Termos e da Política de Privacidade da Plumaa.

**ARAUJO & SILVA LTDA** (nome fantasia: **PLUMAA**)
CNPJ 65.321.886/0001-10
R. Pais Leme, nº 215, Conj. 1713, Pinheiros, São Paulo/SP, CEP 05.424-150`;

const renderInlineBold = (text: string): ReactNode[] => {
	const parts = text.split(/(\*\*.*?\*\*)/g).filter(Boolean);
	return parts.map((part, idx) => {
		if (part.startsWith('**') && part.endsWith('**')) {
			return (
				<strong
					key={`strong-${idx}`}
					className="font-semibold text-gray-800"
				>
					{part.slice(2, -2)}
				</strong>
			);
		}
		return <Fragment key={`text-${idx}`}>{part}</Fragment>;
	});
};

const renderTermsContent = (content: string): ReactNode[] => {
	const lines = content.split('\n');
	const nodes: ReactNode[] = [];
	let i = 0;
	let key = 0;

	while (i < lines.length) {
		const line = lines[i];
		const trimmed = line.trim();

		if (!trimmed || trimmed === '---') {
			i += 1;
			continue;
		}

		if (trimmed.startsWith('## ')) {
			nodes.push(
				<h2
					key={`h2-${key++}`}
					className="pt-6 text-2xl font-semibold text-gray-800"
				>
					{trimmed.slice(3)}
				</h2>,
			);
			i += 1;
			continue;
		}

		if (trimmed.startsWith('- ')) {
			const items: string[] = [];
			while (i < lines.length && lines[i].trim().startsWith('- ')) {
				items.push(lines[i].trim().slice(2));
				i += 1;
			}
			nodes.push(
				<ul
					key={`ul-${key++}`}
					className="list-inside list-disc space-y-2 pl-4"
				>
					{items.map((item, idx) => (
						<li key={`li-${idx}`}>{renderInlineBold(item)}</li>
					))}
				</ul>,
			);
			continue;
		}

		const paragraphLines: string[] = [];
		while (i < lines.length) {
			const current = lines[i].trim();
			if (
				!current ||
				current === '---' ||
				current.startsWith('## ') ||
				current.startsWith('- ')
			) {
				break;
			}
			paragraphLines.push(lines[i]);
			i += 1;
		}

		nodes.push(
			<p key={`p-${key++}`} className="whitespace-pre-line">
				{renderInlineBold(paragraphLines.join('\n').trim())}
			</p>,
		);
	}

	return nodes;
};

const CollaboratorTermsOfUsePage = () => {
	return (
		<main className="mx-auto w-full max-w-3xl px-4 py-12 md:py-20">
			<Helmet>
				<title>Termos de Uso do Colaborador | Plumaa</title>
				<meta
					name="description"
					content="Consulte os Termos e Condições de Uso aplicáveis ao Colaborador da Plumaa na modalidade B2B2C."
				/>
			</Helmet>
			<h1 className="mb-8 text-2xl font-bold text-gray-900 md:text-3xl">
				TERMOS E CONDIÇÕES DE USO — COLABORADOR
			</h1>
			<p className="mb-2 text-base text-zinc-600 md:text-lg">
				<strong>
					Plumaa — Acesso via Empresa Contratante (Modalidade B2B2C)
				</strong>
			</p>
			<p className="mb-8 font-semibold text-gray-800">
				Última atualização: 22 de abril de 2026
			</p>

			<div className="space-y-6 text-justify text-zinc-600">
				{renderTermsContent(COLLABORATOR_TERMS_TEXT).map(
					(node, idx) => (
						<Fragment key={`collab-term-node-${idx}`}>
							{node}
						</Fragment>
					),
				)}
			</div>
		</main>
	);
};

export default CollaboratorTermsOfUsePage;
